<template>
  <div class="user-page">
    <el-button type="primary" @click="onCreateNew">创建新的用户</el-button>
    <div class="searchArea">
      <el-form 
        :inline="true" :model="searchForm" 
        :rules="searchRules" class="demo-form-inline" ref="ruleFormRef"
      >
        <el-form-item label="类型" prop="type">
          <el-select v-model="searchForm.type" class="m-2" placeholder="选择搜索类型">
            <el-option key="_id" label="_id" value="_id"></el-option>
            <el-option key="username" label="username" value="userName"></el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="value" prop="value">
          <el-input v-model="searchForm.value" placeholder="输入要搜索的值" />
        </el-form-item>
        <el-form-item label="创建时间" prop="dataRange">
          <el-date-picker
            v-model="searchForm.dateRange"
            type="daterange"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
          />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="onSearch">搜索</el-button>
        </el-form-item>
        <el-form-item v-if="searchTriggered">
          <el-button @click="onClear">清空</el-button>
        </el-form-item>
      </el-form>
      <el-switch v-model="checksRef.isValid" active-text="只显示高级会员" @change="onClear"/>
      <el-switch v-model="checksRef.inviter" active-text="只显示被邀请会员" @change="onClear"/>
    </div>
    <el-table :data="users" stripe style="width: 100%"  @sort-change="onSort" v-loading="isLoading">
    <el-table-column prop="_id" label="ID" width="150"></el-table-column>
      <el-table-column prop="userName" label="用户" width="150"></el-table-column>
      <el-table-column prop="nickName" label="昵称" width="150"></el-table-column>
      <el-table-column prop="createdAt" label="创建时间" width="150" sortable="custom"></el-table-column>
      <el-table-column prop="updatedAt" label="更新时间" width="150" sortable="custom"></el-table-column>
      <el-table-column prop="type" label="类型" width="150"></el-table-column>
      <el-table-column prop="isValid" label="高级会员" width="150"></el-table-column>
      <el-table-column prop="inviter" label="邀请者" width="150"></el-table-column>
      <el-table-column prop="token" label="Token" width="100"></el-table-column>
      <el-table-column prop="balance" label="代金券" width="100"></el-table-column>
      <el-table-column fixed="right" label="操作">
        <template #default="scope">
          <el-button size="small" @click="triggerEdit(scope.$index)">编辑</el-button>
          <el-button type="danger" size="small" @click="onDelete(scope.row._id)">删除</el-button>
          <el-switch 
            v-model="scope.row.disabled" 
            @change="(disabled) => {toggleDisabled(scope.row._id, disabled)}" 
            active-text="禁用" 
            :style="{'margin-left' : '10px'}"
            active-color="#f56c6c"
          />
        </template>
      </el-table-column>
    </el-table>
    <el-pagination 
      background 
      layout="total,prev, pager, next"
      :total="count" 
      :page-size="PAGE_SIZE"
      v-model:currentPage="currentPage"
      @current-change="onPageChange"
      :style="{'margin-top' : '15px'}"
    >
    </el-pagination>
  <el-dialog
    v-model="dialogVisible"
    :title="editMode ? '编辑用户' : '创建用户'"
    fullscreen
    :destroy-on-close="true"
  >
    <VueForm
      v-model="formData"
      :schema="schema"
      @cancel="onCancel"
      @submit="onSubmit"
    >
    </VueForm>
  </el-dialog>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref, inject } from 'vue';
import { ElMessage, ElMessageBox, ElForm } from 'element-plus'
import dayjs from 'dayjs'
import { forEach, isEmpty, isNumber } from 'lodash-es'
import { useStore } from '../store/index'
import useFilterSearch from '../hooks/useFilterSearch'
type FormInstance = InstanceType<typeof ElForm>
export default defineComponent({
  name: 'User',
  setup() {
    const baseAPIURL = inject('baseAPIURL')
    const store = useStore()
    const dialogVisible = ref(false)
    const editMode = ref('')
    const ruleFormRef = ref<FormInstance>()
    const users = computed(() => store.state.user.users.map(user => {
      user.createdAt = dayjs(user.createdAt).format('YYYY-MM-DD HH:mm') 
      user.updatedAt = dayjs(user.updatedAt).format('YYYY-MM-DD HH:mm')
      return user
    }))
    const count = computed(() => store.state.user.count)
    const isLoading = computed(() => store.getters.isLoading)
    const { 
      currentPage,
      PAGE_SIZE,
      searchForm,
      searchTriggered,
      checksRef,
      performGetList,
      resetValues,
      onSort
    } = useFilterSearch('getUsers', { isValid: false, inviter: false })
    const searchRules = reactive({
      type: [
        {
          required: true,
          message: '请输入搜索类型',
          trigger: 'change',
        },
      ],
      value: [
        {
          required: true,
          message: '请输入搜索值',
          trigger: 'blur',
        },        
      ]
    })
    const formData = ref({
      email: '',
      userName: '',
      nickName: '',
      password: '',
      avatar: '',
      type: '',
      role: '',
      balance: 0,
      token: 0,
      range: [] as Date[]
    })
    const triggerEdit = (index: number) => {
      dialogVisible.value = true
      const password = schema.anyOf[0].properties.password as any
      password["ui:hidden"] = true
      const { _id, email, userName, nickName, avatar, type, role, valiFrom, valiTo, balance, token } = users.value[index]
      editMode.value = _id
      
      formData.value = {
        email,
        password: '',
        userName,
        nickName,
        avatar,
        type,
        role,
        balance,
        token,
        ...( (valiFrom && valiTo) ? { range: [valiFrom, valiTo] } : { range: [] }  )
      }
      // schema.properties.coverImage.default = coverImage
    }
    const schema = reactive({
      type: 'object',
      required: [
        'role'
      ],
      "anyOfSelect": {
        "ui:widget": "RadioWidget",
        "ui:title": "选择账户类型",
      },
      "anyOf": [
        {
          "ui:showTitle": true,
          "title": "邮箱创建",
          "properties": { 
            "type": {
              "ui:hidden": true,
              "type": "string",
              "default": "email",
              "const": "email"
            },
            email: {
              type: 'string',
              title: '邮箱地址',
            },
            password: {
              type: 'string',
              title: '用户密码',
              "ui:hidden": false
            },
            userName: {
              type: 'string',
              title: '用户名称',
            },
            nickName: {
              type: 'string',
              title: '用户昵称',
            },
          }
        },
        {
          "ui:showTitle": true,
          "title": "手机创建",
          "properties": { 
            "type": {
              "ui:hidden": true,
              "type": "string",
              "default": "cellphone",
              "const": "cellphone"
            },
            cellphone: {
              type: 'string',
              title: '手机号码',
            },
          }
        },
        {
          "ui:showTitle": true,
          "title": "Oauth创建",
          "properties": { 
            "type": {
              "ui:hidden": true,
              "type": "string",
              "default": "oauth",
              "const": "oauth"
            },
            email: {
              type: 'string',
              title: 'OauthID',
            },
          }
        }
      ],
      properties: {
        role: {
            title: "用户角色",
            type: "string",
            "enum": [
              "member",
              "admin",
            ],
            "enumNames": [
              "普通用户",
              "管理员",
            ],
            default: 'member'
        },
        "range": {
          "title": "会员开始结束时间",
          "type": "array",
          "format": "date-time",
          "items": {
            "type": "string"
          }
        },
        token: {
            type: 'number',
            title: 'Token 数量'
        },
        balance: {
          type: 'number',
           title: 'Balance 数量'
        },
        avatar:{
          "anyOfSelect": {
              "ui:widget": "RadioWidget",
              "ui:title": "头像图片",
              "ui:options": {}
          },
          anyOf: [
            {
              title: "上传图片",
              type: "string",
              default: "",
              "ui:action": `${baseAPIURL}/api/upload`,
              "ui:with-credentials": true,
              "ui:list-type": "picture",
              "ui:widget": "UploadWidget",
              "ui:btnText": "上传头图"
            }, 
            {
              title: "直接填写URL",
              type: "string",
            }
          ]
        }
      }
    })
    
    const onCreateNew = () => {
      const password = schema.anyOf[0].properties.password as any
      password["ui:hidden"] = false
      formData.value = {
        email: '',
        userName: '',
        nickName: '',
        password: '',
        avatar: '',
        type: 'email',
        role: '',
        token: 5000,
        balance: 0,
        range: []
      }
      dialogVisible.value = true
      editMode.value = ''  
    }
    const onSearch = () => {
      if (!ruleFormRef.value) return
      ruleFormRef.value.validate(async (valid) => {
        // 两种情况，
        if (valid || (searchForm.dateRange && !valid)) {
          if (ruleFormRef.value) {
            ruleFormRef.value.clearValidate('type')
            ruleFormRef.value.clearValidate('value')
          }
          searchTriggered.value = true
          currentPage.value = 1
          await performGetList()
        } else {
          return false
        }
      })
    }
    const resetSearch = () => {
      if (!ruleFormRef.value) return
      searchForm.dateRange = ''
      ruleFormRef.value.resetFields()
      resetValues()
    }
    const onClear = async () => {
      resetSearch()
      await performGetList()
    }
    const toggleDisabled = async (id:string, disabled: boolean) => {
      await store.dispatch('updateUser', { urlParams: { id }, data: { disabled } })
      ElMessage({
        type: 'success',
        message: '更新成功',
        duration: 1000
      })
    }
    const onCancel = () => {
      dialogVisible.value = false
    }
    const formatData = ( editMode: boolean ) => {
      const data: Record<string, any> = {}
      forEach(formData.value, (value, key) => {
        // isEmpty 会排除 number，所以 number 要自己处理一下
        if (!isEmpty(value)) {
          if (key === 'range'  && Array.isArray(value) && value.length === 2) {
            data.valiFrom = new Date(value[0] as any)
            data.valiTo = new Date(value[1] as any)
            // 只有创建模式才需要添加额外的字段，
            // 编辑模式，现在只修改几种值，range/balance/token
          } else if (!editMode) {
            data[key] = value
          }
        }
        // balance and token
        if (isNumber(value)) {
          // balance 设置为 0 的时候不生效，因为默认为零
          if (key === 'balance' && value !== 0) {
            data[key] = value
          } else if (key === 'token') {
            data[key] = value
          }
        }
      })
      return data
    }
    const onSubmit = async () => {
      if (editMode.value === '') {
        const finalData = formatData(false)
        console.log('create', finalData)
        await store.dispatch('createUser', { data: finalData })
        await store.dispatch('getUsers')
        ElMessage({
          type: 'success',
          message: '创建成功',
          duration: 1000
        })
        dialogVisible.value = false
      } else {
        const finalData = formatData(true)
        console.log('edit', finalData)
        await store.dispatch('updateUser', { urlParams: { id: editMode.value }, data: finalData })
        // await store.dispatch('getUsers', { searchParams: { currentPage: 1, pageSize: PAGE_SIZE }})
        ElMessage({
          type: 'success',
          message: '更新成功',
          duration: 1000
        })
        dialogVisible.value = false
      }
    }
    const onPageChange = async () => {
      await performGetList()
    }
    const onDelete = (id: string) => {
      ElMessageBox.confirm('是否要永久删除该用户？特别注意该用户下的简历会受影响！',  '删除用户', {
        confirmButtonText: '确认删除',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        await store.dispatch('deleteUser', { urlParams: { id }})
        ElMessage({
          type: 'success',
          message: '删除成功',
          duration: 1000
        })
      }).catch((e) => {
        throw e
      })
    }
    onMounted(async () => {
      await store.dispatch('getUsers', { searchParams: { currentPage: 1, pageSize: PAGE_SIZE }})
    })
    return {
      users,
      schema,
      formData,
      triggerEdit,
      dialogVisible,
      onCancel,
      onSubmit,
      onCreateNew,
      editMode,
      onDelete,
      toggleDisabled,
      count,
      PAGE_SIZE,
      onPageChange,
      searchForm,
      searchRules,
      ruleFormRef,
      onSearch,
      onClear,
      searchTriggered,
      currentPage,
      onSort,
      checksRef,
      isLoading
    }
  }
});
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style>
.user-page .searchArea {
  margin: 15px 0;
  display: flex;
  align-items: center;
}
.user-page .searchArea .el-form-item {
  margin-bottom: 0;
}
</style>

